/** Лига и друзья — представление без стора, всё приходит пропсами. */
import { Button, Icon, Panel, WindowShell, compactCoins } from "../kit";
import {
  type FriendRewardInfo,
  type FriendsResponse,
  type LeagueResponse,
  type LeagueRow,
} from "@newfungame/contracts";
import { getLocale } from "../../../i18n";
import { useT } from "../../../app";
import "./styles.css";

const LEAGUE_TIER_KEYS = ["bronze", "silver", "gold", "platinum", "diamond"] as const;
const tierName = (t: ReturnType<typeof useT>, tier: number) =>
  t(`win.league.tier.${LEAGUE_TIER_KEYS[tier] ?? "bronze"}`);

export interface LeagueViewProps {
  tab: "group" | "friends";
  /** Лиги только для аккаунтов Telegram. */
  guest: boolean;
  tier: number;
  unclaimed: number;
  group: LeagueResponse | null;
  friends: FriendsResponse | null;
  daysLeft: number | null;
  onTab(tab: "group" | "friends"): void;
  onClaim(): void;
  onInvite(url: string): void;
  onTelegram(): void;
  onClose(): void;
}

function FriendRewardNote({ reward: r }: { reward: FriendRewardInfo }) {
  const t = useT();
  return (
    <p class="win-note" data-testid="league-friend-reward">
      {t("win.league.friendReward", { wave: r.wave, coins: compactCoins(r.coins, getLocale()) })}{" "}
      {r.earned >= r.cap
        ? t("win.league.friendCap", { cap: r.cap })
        : r.earned > 0 && t("win.league.friendEarned", { n: r.earned, cap: r.cap })}
    </p>
  );
}

function LeagueRows(p: { rows: LeagueRow[]; up?: number; down?: number; testid: string }) {
  const t = useT();
  const size = p.rows.length;
  const zone = (place: number) => {
    if (place <= (p.up ?? 0)) return " win-league__row--up";
    return (p.down ?? 0) > 0 && place > size - p.down! ? " win-league__row--down" : "";
  };
  return (
    <ol class="win-league" data-testid={p.testid}>
      {p.rows.map((r) => (
        <li key={r.place} class={`win-league__row${zone(r.place)}${r.me ? " win-league__row--me" : ""}`}>
          <span class="win-season__num">{r.place}</span>
          <span class="win-league__name">{r.me ? t("win.league.you", { name: r.name }) : r.name}</span>
          <span class="win-league__points">{r.points}</span>
        </li>
      ))}
    </ol>
  );
}

export function LeagueView(p: LeagueViewProps) {
  const t = useT();
  const close = (
    <div class="win-actions">
      <Button tone="link" onClick={p.onClose} data-testid="league-close">
        {t("win.tasks.close")}
      </Button>
    </div>
  );
  if (p.guest) {
    return (
      <WindowShell kind="league" title={t("win.league.title")}>
        <p class="win-note" data-testid="league-guest">
          {t("win.league.guest")}
        </p>
        <div class="win-actions">
          <Button tone="blue" onClick={p.onTelegram} data-testid="league-telegram">
            {t("win.pay.telegram")}
          </Button>
        </div>
        {close}
      </WindowShell>
    );
  }
  const last = p.group?.last ?? null;
  const rows = p.group?.group ?? null;
  return (
    <WindowShell kind="league" title={t("win.league.title")}>
      <div class="win-league__tabs" role="tablist">
        {(["group", "friends"] as const).map((id) => (
          <Button
            key={id}
            tone={p.tab === id ? "blue" : "gray"}
            onClick={() => p.onTab(id)}
            data-testid={`league-tab-${id}`}
          >
            {t(`win.league.tab.${id}`)}
          </Button>
        ))}
      </div>
      {p.tab === "group" ? (
        <>
          <p class="win-tasks__reset" data-testid="league-tier">
            <Icon name="clock" size={18} />
            {tierName(t, p.tier)}
            {p.daysLeft !== null && ` · ${t("win.league.left", { d: p.daysLeft })}`}
          </p>
          {last && (
            <Panel testid="league-last">
              {t(`win.league.result.${last.outcome}`, {
                place: last.place,
                size: last.size,
                tier: tierName(t, last.tier),
              })}
            </Panel>
          )}
          {p.unclaimed > 0 && (
            <div class="win-actions">
              <Button tone="green" onClick={p.onClaim} data-testid="league-claim">
                {t("win.league.claim")}{" "}
                <span class="win-price">
                  <Icon name="crystal" size={18} /> {p.unclaimed}
                </span>
              </Button>
            </div>
          )}
          {rows ? (
            <>
              <LeagueRows rows={rows.rows} up={rows.up} down={rows.down} testid="league-rows" />
              {rows.rows.length < 2 && (
                <p class="win-note" data-testid="league-alone">
                  {t("win.league.alone")}
                </p>
              )}
              <p class="win-note">{t("win.league.rules")}</p>
            </>
          ) : (
            p.group && (
              <p class="win-note" data-testid="league-empty">
                {t("win.league.empty")}
              </p>
            )
          )}
        </>
      ) : (
        <>
          {p.friends && <LeagueRows rows={p.friends.rows} testid="league-friends" />}
          {p.friends && p.friends.rows.length < 2 && (
            <p class="win-note" data-testid="league-no-friends">
              {t("win.league.noFriends")}
            </p>
          )}
          {p.friends?.reward && p.friends.inviteUrl && <FriendRewardNote reward={p.friends.reward} />}
          {p.friends?.inviteUrl && (
            <div class="win-actions">
              <Button tone="green" onClick={() => p.onInvite(p.friends!.inviteUrl!)} data-testid="league-invite">
                {t("win.league.invite")}
              </Button>
            </div>
          )}
        </>
      )}
      {close}
    </WindowShell>
  );
}
